<template>
  <div class="home-page">

    <!-- ===== LOGGED-IN DASHBOARD ===== -->
    <div v-if="loggedIn">

      <!-- Welcome Banner -->
      <div class="dashboard-hero">
        <div class="dashboard-hero__text">
          <h1 class="dashboard-hero__title">Welcome back</h1>
          <p class="dashboard-hero__sub">Pick up where you left off, or try a different challenge.</p>
        </div>
        <div class="dashboard-hero__stats">
          <div class="stat-pill">
            <span class="stat-pill__num">14</span>
            <span class="stat-pill__label">Activities</span>
          </div>
          <div class="stat-pill stat-pill--accent">
            <span class="stat-pill__num">4</span>
            <span class="stat-pill__label">Categories</span>
          </div>
        </div>
      </div>

      <!-- Quick Jump Bar -->
      <div class="quick-bar">
        <a href="#section-english" class="quick-pill">🔤 Verbal</a>
        <a href="#section-maths" class="quick-pill">🔢 Quantitative</a>
        <a href="#section-tools" class="quick-pill">🧮 Tools</a>
        <a href="#section-colours" class="quick-pill">🎨 Colours</a>
      </div>

      <!-- ── VERBAL ── -->
      <section id="section-english" class="subject-section">
        <div class="subject-section__header subject-section__header--english">
          <span class="subject-section__icon">🔤</span>
          <div>
            <h2 class="subject-section__title">Verbal</h2>
            <p class="subject-section__desc">Vocabulary, spelling, word recognition, and word search - from foundational literacy to advanced verbal reasoning.</p>
          </div>
        </div>
        <div class="feature-grid">

          <!-- <router-link to="/alphabets" class="feat-card feat-card--alphabet">
            <div class="feat-card__icon-wrap">🔡</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Alphabets</h3>
              <p class="feat-card__desc">A–Z reference with capitals, vowels, and consonants. A solid starting point for ESL learners and quick refreshers.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link> -->

          <router-link to="/wordGenerator" class="feat-card feat-card--engwords">
            <div class="feat-card__icon-wrap">📖</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Word Generator</h3>
              <p class="feat-card__desc">Study a word, hide it, then spell it from memory. Spans everyday vocabulary through to expert-level academic and scientific terms.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/missingLetter" class="feat-card feat-card--wordgame">
            <div class="feat-card__icon-wrap">🔍</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Missing Letter</h3>
              <p class="feat-card__desc">Identify the letter that completes each word. Difficulty scales from short common words up to complex vocabulary.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/findWords" class="feat-card feat-card--findwords">
            <div class="feat-card__icon-wrap">🔎</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Find Words</h3>
              <p class="feat-card__desc">Trace connected letters across a grid to form hidden words. A useful exercise in visual pattern recognition.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/wordSearch" class="feat-card feat-card--wordsearch">
            <div class="feat-card__icon-wrap">🔠</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Word Search</h3>
              <p class="feat-card__desc">Scan an 11×11 grid and drag to cross out all the hidden words, placed horizontally, vertically, and diagonally.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

        </div>
      </section>

      <!-- ── QUANTITATIVE ── -->
      <section id="section-maths" class="subject-section">
        <div class="subject-section__header subject-section__header--maths">
          <span class="subject-section__icon">🔢</span>
          <div>
            <h2 class="subject-section__title">Quantitative</h2>
            <p class="subject-section__desc">Arithmetic, fractions, sequences, and geometry - with four difficulty tiers across every activity.</p>
          </div>
        </div>
        <div class="feature-grid">

          <router-link to="/sortChallenge" class="feat-card feat-card--numbers">
            <div class="feat-card__icon-wrap">🔢</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Sort Challenge</h3>
              <p class="feat-card__desc">Arrange integers, negatives, and decimals in order. Expert mode reaches into the hundreds and includes mixed number types.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/operatorIdentifier" class="feat-card feat-card--arithmetic">
            <div class="feat-card__icon-wrap">➕</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Operator Identifier</h3>
              <p class="feat-card__desc">Determine which arithmetic operator (+, −, ×, ÷) makes each equation true.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/addMinus" class="feat-card feat-card--addminus">
            <div class="feat-card__icon-wrap">➕➖</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Add &amp; Subtract</h3>
              <p class="feat-card__desc">Four difficulty tiers from single-digit arithmetic to four-digit calculations with decimals and real-world word problems.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/timesDivide" class="feat-card feat-card--times">
            <div class="feat-card__icon-wrap">✖️<b>÷</b></div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Multiply &amp; Divide</h3>
              <p class="feat-card__desc">Master multiplication and division through interactive games.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/timesTablesTrainer" class="feat-card feat-card--timestables">
            <div class="feat-card__icon-wrap">📋</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Times Tables Trainer</h3>
              <p class="feat-card__desc">Drill times tables from 1× to 12× until they stick.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/fractionsQuiz" class="feat-card feat-card--fractions">
            <div class="feat-card__icon-wrap">½</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Fractions Quiz</h3>
              <p class="feat-card__desc">Understand and compare fractions with visual quizzes.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/numberSequence" class="feat-card feat-card--sequence">
            <div class="feat-card__icon-wrap">🔗</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Number Sequence</h3>
              <p class="feat-card__desc">Find the pattern and complete the number sequence.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

          <router-link to="/shapeRecognition" class="feat-card feat-card--shapes">
            <div class="feat-card__icon-wrap">🔷</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Shape Recognition</h3>
              <p class="feat-card__desc">Identify 2D and 3D shapes and learn their properties.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>

        </div>
      </section>

      <!-- ── TOOLS ── -->
      <section id="section-tools" class="subject-section">
        <div class="subject-section__header subject-section__header--tools">
          <span class="subject-section__icon">🧮</span>
          <div>
            <h2 class="subject-section__title">Tools</h2>
            <p class="subject-section__desc">Handy utilities to support your studies.</p>
          </div>
        </div>
        <div class="feature-grid feature-grid--single">
          <router-link to="/standardCalc" class="feat-card feat-card--calc">
            <div class="feat-card__icon-wrap">🧮</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Calculator</h3>
              <p class="feat-card__desc">A full arithmetic calculator to check your work or explore calculations.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>
        </div>
      </section>

      <!-- ── COLOURS ── -->
      <section id="section-colours" class="subject-section">
        <div class="subject-section__header subject-section__header--colours">
          <span class="subject-section__icon">🎨</span>
          <div>
            <h2 class="subject-section__title">Colours</h2>
            <p class="subject-section__desc">Colour identification and recognition across the visible spectrum.</p>
          </div>
        </div>
        <div class="feature-grid feature-grid--single">
          <router-link to="/colours" class="feat-card feat-card--colours">
            <div class="feat-card__icon-wrap">🌈</div>
            <div class="feat-card__body">
              <h3 class="feat-card__title">Colours Explorer</h3>
              <p class="feat-card__desc">Identify and name colours across the full visible spectrum. Useful for design, art, and general knowledge.</p>
            </div>
            <span class="feat-card__arrow">→</span>
          </router-link>
        </div>
      </section>
    </div>

    <!-- ===== LOGGED-OUT HERO ===== -->
    <div class="hero-section" v-else>
      <div class="hero-badge">Free · No download · Works on any device</div>
      <h1 class="hero-section__title">Sharpen the skills that matter</h1>
      <h2 class="hero-section__subtitle">Verbal · Quantitative · Colours · Tools</h2>
      <p class="hero-section__text">
        A focused practice platform for everyone - whether you're preparing for an assessment,
        brushing up after a break, or simply keeping your mind engaged.
      </p>
      <div class="cta-group">
        <router-link class="btn-primary-app" to="/signup">Create a free account</router-link>
        <router-link class="btn-outline-app" to="/signin">Sign in</router-link>
      </div>
      <div class="hero-subjects">
        <span class="hero-subject-chip">🔤 Verbal</span>
        <span class="hero-subject-chip">🔢 Quantitative</span>
        <span class="hero-subject-chip hero-subject-chip--locked">🔒 More inside</span>
      </div>
    </div>

  </div>
</template>

<script>
import { createUserProfileDocument, auth } from "../firebase/firebase.utils";

export default {
  created() {
    auth.onAuthStateChanged((user) => {
      this.loggedIn = !!user;
    });
  },
  mounted() {
    this.unsubscribeFromAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userRef = await createUserProfileDocument(user);
        userRef.onSnapshot((snapShot) => {
          console.log(snapShot);
        });
      }
      this.loggedIn = !!user;
    });
  },
  beforeDestroy() {
    this.unsubscribeFromAuth();
  },
  data() {
    return {
      loggedIn: false,
      unsubscribeFromAuth: null,
    };
  },
};
</script>

<style scoped>
.home-page {
  max-width: 1100px;
  margin: 0 auto;
}

/* Badge above hero title */
.hero-badge {
  display: inline-block;
  background: var(--color-primary-light);
  color: var(--color-primary-dark);
  border: 1px solid var(--color-primary);
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.3rem 0.9rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
}

/* Subject preview chips on logged-out hero */
.hero-subjects {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border);
}

.hero-subject-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.85rem;
  background: var(--color-surface-solid);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-muted);
  white-space: nowrap;
}

.hero-subject-chip--locked {
  background: var(--color-primary-light);
  border-color: var(--color-primary);
  color: var(--color-primary-dark);
}
</style>
